/**
 * The ring of text itself.
 *
 * Shared by edit.jsx and save.jsx so the editor preview and the saved markup
 * cannot drift — a static-save block that builds its markup twice is the
 * classic source of "block contains unexpected content" errors.
 */

/**
 * SVG user units are the block's own pixels: the viewBox is sized to `size`
 * rather than a normalised 0–100 box, so a font size set through the block's
 * typography controls lands on the ring at exactly the px the author picked
 * instead of being rescaled by the badge's diameter.
 *
 * @param {Object} attributes Block attributes.
 * @return {Object} Radius, circumference and the arc path.
 */
export function getRingGeometry({ size, textInset }) {
  const centre = size / 2;
  // Keep the path inside the circle even if the inset is dialled past the middle.
  const radius = Math.max(1, centre - textInset);

  return {
    radius,
    circumference: 2 * Math.PI * radius,
    // Starts at twelve o'clock and sweeps clockwise, so the phrase begins where
    // a reader looks first.
    path: `M ${centre},${centre - radius} a ${radius},${radius} 0 1,1 0,${
      radius * 2
    } a ${radius},${radius} 0 1,1 0,${-radius * 2}`,
  };
}

/**
 * One phrase per repeat, each followed by its separator, so the ring closes on
 * a separator rather than butting the last word against the first.
 *
 * The padding around each separator is non-breaking spaces, not ordinary ones.
 * SVG runs XML whitespace processing over text content: runs of plain spaces
 * collapse and, fatally here, leading and trailing ones are stripped. The final
 * separator's trailing space is exactly that — so an ordinary space leaves the
 * ring closing on ".Thank", tight, while every other separator has room. A
 * no-break space is not whitespace to that algorithm and survives.
 *
 * @param {Object} attributes Block attributes.
 * @return {string} The full string laid along the path.
 */
export function getRingText({ text, separator, repeat }) {
  const phrase = (text || '').trim();

  if (!phrase) {
    return '';
  }

  const nbsp = '\u00a0';
  const mark = separator ? separator.trim() : '';
  const gap = mark ? `${nbsp}${mark}${nbsp}` : `${nbsp}${nbsp}`;

  return Array.from({ length: Math.max(1, repeat) }, () => phrase).join(gap) + gap;
}

/**
 * @param {Object} props            Component props.
 * @param {Object} props.attributes Block attributes.
 * @return {JSX.Element|null} The ring, or nothing when there is no phrase.
 */
export default function Ring({ attributes }) {
  const { size } = attributes;
  const { circumference, path } = getRingGeometry(attributes);
  const content = getRingText(attributes);

  if (!content) {
    return null;
  }

  return (
    <svg
      className="brandy-circular-badge__ring"
      viewBox={`0 0 ${size} ${size}`}
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      {/*
        The arc rides on textPath's own `path` attribute rather than an id in
        <defs>. Two badges on one page would otherwise need two unique ids, and
        an id that has to survive save() cannot come from useInstanceId without
        the editor and the front end disagreeing on it.

        textLength pinned to the circumference makes the phrase close the ring
        exactly, whatever its length — without it a short phrase leaves a gap at
        twelve o'clock and a long one overlaps itself. `spacing` stretches the
        gaps and leaves the glyphs alone, so letterforms are never distorted.
      */}
      <text textLength={circumference} lengthAdjust="spacing">
        <textPath path={path}>{content}</textPath>
      </text>
    </svg>
  );
}
